<template>
  <DynamicModal
    :showModal="showModalProp"
    :persistent="false"
    @clickOutside="closeModal"
    title="Markup Calculator"
    maxWidth="600px"
  >
    <v-container fluid slot="title-buttons" class="py-1">
      <v-row justify="end">
        <v-btn
          icon
          depressed
          color="primary"
          @click="closeModal"
        >
          <v-icon color="white">close</v-icon>
        </v-btn>
      </v-row>
    </v-container>
    <v-container fluid slot="content">
      <v-form ref="PayAndBillDetailsForm" lazy-validation>
        <v-row>
          <v-col cols="12" class="px-0">
            <v-card-text>Enter Any Two Values (Pay Rate, Bill Rate or Markup%) and Hit "Calculate" to Get the Third Value.</v-card-text>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" sm="6" class="pt-0">
            <v-select
              label="Consultant Type"
              :items="employeeTypes"
              item-text="text"
              item-value="id"
              v-model="employeeType"
              hide-details
              outlined
            ></v-select>
          </v-col>
          <v-col cols="6" class="pt-0">
            <FormattedNumberField
              label="VMS Fee"
              :value="vmsFee"
              appendIcon="percent"
              :hideDetails="true"
              @valueChange="(newValue) => this.vmsFee = newValue"
            ></FormattedNumberField>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="6" sm="4">
            <FormattedNumberField
              label="Pay Rate"
              :value="payRate"
              prepend-inner-icon="attach_money"
              :required="false"
              :hide-details="true"
              @valueChange="(newValue) => this.payRate = newValue"
              @blur="setPayRateType"
            ></FormattedNumberField>
          </v-col>
          <v-col cols="6" sm="4">
            <RateTypeDropdown
              :value="payAndBillRateType"
              :isRequired="true"
              :hideDetails="true"
              @rateTypeChange="(newValue) => this.payAndBillRateType = newValue"
            ></RateTypeDropdown>
          </v-col>
          <v-col cols="6" sm="4">
            <FormattedNumberField
              label="Total Pay Rate"
              :value="totalPayRate"
              prepend-inner-icon="attach_money"
              :readonly="true"
              :hide-details="true"
            ></FormattedNumberField>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="6" sm="4">
            <FormattedNumberField
              label="Bill Rate"
              :value="billRate"
              :required="false"
              prepend-inner-icon="attach_money"
              :hide-details="true"
              @valueChange="(newValue) => this.billRate = newValue"
              @blur="setBillRateType"
            ></FormattedNumberField>
          </v-col>
          <v-col cols="6" sm="4">
            <RateTypeDropdown
              :value="payAndBillRateType"
              :isRequired="true"
              :hideDetails="true"
              @rateTypeChange="(newValue) => this.payAndBillRateType = newValue"
            ></RateTypeDropdown>
          </v-col>
          <v-col cols="6" sm="4">
            <FormattedNumberField
              label="Net Bill Rate"
              :value="netBillRate"
              prepend-inner-icon="attach_money"
              :readonly="true"
              :hide-details="true"
            ></FormattedNumberField>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="6">
            <FormattedNumberField
              label="Markup %"
              :value="markupPercentage"
              append-icon="percent"
              :hide-details="true"
              @valueChange="(newValue) => this.markupPercentage = newValue"
              ></FormattedNumberField>
            </v-col>
          <v-col cols="6">
            <FormattedNumberField
              label="Gross Profit"
              :value="grossProfit"
              prepend-inner-icon="attach_money"
              :readonly="true"
              :hide-details="true"
            ></FormattedNumberField>
          </v-col>
        </v-row>
        <v-row>
          <v-col class="my-1 py-0">
            <v-card-text class="my-0 py-0 mx-0 px-0">{{ totalPayRateDescription }}</v-card-text>
          </v-col>
        </v-row>
        <v-row>
          <v-col class="my-1 py-0">
            <v-card-text class="my-0 py-0 mx-0 px-0">{{ netBillRateDescription }}</v-card-text>
          </v-col>
        </v-row>
        <v-row>
          <v-col class="my-1 py-0">
            <v-card-text class="my-0 py-0 mx-0 px-0">{{ grossProfitDescription }}</v-card-text>
          </v-col>
        </v-row>
      </v-form>
      <MessageBox
          ref="messageBox"
          message="Enter Any Two Values (Pay Rate, Bill Rate or Markup%) to Get the Third Value "
          color="error"
          closeTextColor="white"
        ></MessageBox>
    </v-container>
    <v-container fluid slot="buttons" class="pb-0 mb-0">
      <v-row>
        <v-col cols="6" sm="4">
          <v-btn
            color="primary"
            block
            @click="calculatePayAndBillDetails"
          >
            Calculate
          </v-btn>
        </v-col>
        <v-col cols="6" sm="4">
          <v-btn
            outlined
            block
            @click="clearPayAndBillDetails"
          >
            Clear
          </v-btn>
        </v-col>
        <v-col cols="12" sm="4">
          <v-btn
            block
            depressed
            outlined
            @click="closeModal"
          >
            Close
          </v-btn>
        </v-col>
      </v-row>
    </v-container>
  </DynamicModal>
</template>
<script>
import { mapActions } from 'vuex'
import { mapFields } from '@/vueHelpers/vuex'
import { RateTypeDropdown } from '@/components/shared/dropdowns'
import { FormattedNumberField } from '@/components/shared/textFields'
import {
  getNetBillRate,
  getTotalPayRate,
  getOverheadPercent,
  getMarkup
} from '@/lib/grossProfitCalculator'
import { formatPercent, formatCurrency, getEmployeeTypeLabel } from '@/lib/display'
import { nextTick } from 'vue'
import { HOURLY, MONTHLY, ANNUAL } from '@/lib/constants/rateTypes'
import DynamicModal from '@/components/dialogs/DynamicModal'
import {
  EMPLOYEE_WITH_BENEFITS,
  EMPLOYEE_NO_BENEFITS,
  CORP_TO_CORP,
  INDEPENDENT_CONTRACTOR
} from '@/lib/constants/employeeTypes'
import { MessageBox } from '@/components/shared/messages'

export default {
  components: {
    RateTypeDropdown,
    FormattedNumberField,
    DynamicModal,
    MessageBox
  },
  data () {
    return {
      EMPLOYEE_WITH_BENEFITS,
      EMPLOYEE_NO_BENEFITS,
      CORP_TO_CORP,
      INDEPENDENT_CONTRACTOR,
      payAndBillRateType: null,
      payRate: null,
      billRate: null,
      employeeType: null,
      vmsFee: 0,
      showModalProp: false,
      totalPayRate: null,
      totalPayRateDescription: 'Total Pay Rate = Pay Rate + Overhead',
      netBillRate: null,
      netBillRateDescription: 'Net Bill Rate = Bill Rate - VMS Fee',
      grossProfit: null,
      grossProfitDescription: 'Gross Profit = Net Bill Rate - Total Pay Rate',
      markupPercentage: null,
      employeeTypes: [
        { id: INDEPENDENT_CONTRACTOR, text: '1099' },
        { id: CORP_TO_CORP, text: 'C2C' },
        { id: EMPLOYEE_NO_BENEFITS, text: 'W2 no Benefits' },
        { id: EMPLOYEE_WITH_BENEFITS, text: 'W2 + Benefits' }
      ]
    }
  },
  computed: {
    ...mapFields('settings', ['payRateSettings'])
  },
  methods: {
    ...mapActions('settings', ['getSettings']),
    showModal () {
      this.showModalProp = true
    },
    closeModal () {
      this.showModalProp = false
    },
    isFilledOut () {
      return this.employeeType ? true : 'Please choose the consultant type'
    },
    async calculatePayAndBillDetails () {
      if (this.$refs.PayAndBillDetailsForm.validate()) {
        let fieldsFilled = 0
        fieldsFilled += this.payRate != null ? 1 : 0
        fieldsFilled += this.billRate != null ? 1 : 0
        fieldsFilled += this.markupPercentage != null ? 1 : 0
        if (fieldsFilled >= 2) {
          if (!this.employeeType) {
            this.employeeType = INDEPENDENT_CONTRACTOR
          }
          if (this.payRate !== null && this.billRate == null && this.markupPercentage !== null) {
            this.billRate =
              this.calculateBillRate(
                this.payRate,
                getOverheadPercent(this.employeeType, this.payRateSettings),
                this.markupPercentage,
                this.vmsFee,
                0
              )
          } else if (this.payRate == null && this.billRate !== null && this.markupPercentage !== null) {
            this.payRate =
            this.calculatePayRate(
              this.billRate,
              this.vmsFee,
              this.markupPercentage,
              getOverheadPercent(this.employeeType, this.payRateSettings),
              0
            )
          }
          await nextTick()
          this.totalPayRate = getTotalPayRate(getOverheadPercent(this.employeeType, this.payRateSettings), this.payRate)
          this.totalPayRateDescription = `${this.getTypeLabel(this.payAndBillRateType)} Total Pay Rate (${formatCurrency(this.totalPayRate)}) = Pay Rate (${formatCurrency(this.payRate)}) + ${getEmployeeTypeLabel(this.employeeType)} Overhead (${formatPercent(getOverheadPercent(this.employeeType, this.payRateSettings))})`
          this.netBillRate = getNetBillRate(this.vmsFee, this.billRate)
          this.netBillRateDescription = `${this.getTypeLabel(this.payAndBillRateType)} Net Bill Rate (${formatCurrency(this.netBillRate)}) = Bill Rate (${formatCurrency(this.billRate)}) - VMS Fee (${formatPercent(this.vmsFee)})`
          this.grossProfit = this.netBillRate - this.totalPayRate
          this.grossProfitDescription = `${this.getTypeLabel(this.payAndBillRateType)} Gross Profit (${formatCurrency(this.grossProfit)}) = Net Bill Rate (${formatCurrency(this.netBillRate)}) - Total Pay Rate (${formatCurrency(this.totalPayRate)})`
          if (this.payRate !== null && this.billRate !== null) {
            this.markupPercentage = getMarkup(this.totalPayRate, this.grossProfit).toFixed(2)
          }
        } else {
          this.$refs.messageBox.show()
        }
      }
    },
    calculateBillRate (payRate, overheadPercentage, markupPercentage, vmsFeePercentage, expensesDeduction) {
      return (((payRate * (1 + (overheadPercentage / 100))) * (1 + (markupPercentage / 100))) + expensesDeduction) / (1 - (vmsFeePercentage / 100))
    },
    calculatePayRate (billRate, vmsFeePercentage, markupPercentage, overheadPercentage, expensesDeduction) {
      return ((billRate * (1 - (vmsFeePercentage / 100))) - expensesDeduction) / ((1 + (markupPercentage / 100)) * (1 + (overheadPercentage / 100)))
    },
    clearPayAndBillDetails () {
      this.payRate = null
      this.billRate = null
      this.payAndBillRateType = null
      this.employeeType = null
      this.vmsFee = 0
      this.clearCalculatedFields()
    },
    clearCalculatedFields () {
      this.totalPayRate = null
      this.totalPayRateDescription = 'Total Pay Rate = Pay Rate + Overhead'
      this.netBillRate = null
      this.netBillRateDescription = 'Net Bill Rate = Bill Rate - VMS Fee'
      this.markupPercentage = null
      this.grossProfit = null
      this.grossProfitDescription = 'Gross Profit = Net Bill Rate - Total Pay Rate'
    },
    setPayRateType () {
      this.setRateType(this.payRate)
    },
    setBillRateType () {
      this.setRateType(this.billRate)
    },
    setRateType (newValue) {
      if (newValue && !this.payAndBillRateType) {
        if (newValue < 500) {
          this.payAndBillRateType = HOURLY
        } else if (newValue < 19999) {
          this.payAndBillRateType = MONTHLY
        } else if (newValue >= 20000) {
          this.payAndBillRateType = ANNUAL
        }
      }
    },
    getTypeLabel (type) {
      switch (type) {
        case HOURLY:
          return 'Hourly'
        case MONTHLY:
          return 'Monthly'
        case ANNUAL:
          return 'Annual'
        default:
          return ''
      }
    }
  },
  async created () {
    if (!this.payRateSettings) {
      await this.getSettings()
    }
  }
}
</script>
